import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";

import "../styles/generalStyle.css";
import "./Landing.css";
import "./CancionCompartir.css";

import { useLanguage } from "../i18n/LanguageContext";
import logoMark from "../assets/landing/logo_512.png";
import LanguageDropdown from "../components/LanguageDropdown";


function GoldPlayer({ title, src, t, descargar = true}) {
  const audioRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (playing) audio.pause();
      else await audio.play();
    } catch (e) {
      console.error("Audio play error:", e);
    }
  };

  const onTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setProgress(audio.currentTime || 0);
  };

  const onLoaded = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setDuration(audio.duration || 0);
  };

  const onSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const value = Number(e.target.value);
    audio.currentTime = value;
    setProgress(value);
  };

  const format = (s) => {
    if (!s || Number.isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  // ✅ nombre de archivo bonito (opcional)
  const fileName = `${(title || "cancion").replace(/\s+/g, "_")}.mp3`;

  return (
    <div className="gold-player">
      <div className="gold-title">{title}</div>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoaded}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />

      <div className="gold-controls">
        <button
          type="button"
          className="gold-play"
          onClick={togglePlay}
          aria-label={playing ? t("share.player.pause") : t("share.player.play")}
          title={playing ? t("share.player.pause") : t("share.player.play")}
        >
          {playing ? "❚❚" : "▶"}
        </button>

        <div className="gold-bar-wrap">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={Math.min(progress, duration || 0)}
            onChange={onSeek}
            className="gold-bar"
          />
          <div className="gold-time">
            {format(progress)} / {format(duration)}
          </div>
        </div>

      
        {/* ✅ DESCARGA (solo si descargar === true) */}
        {src && descargar !== false ? (
          <a
            className="gold-download"
            href={src}
            download={fileName}
            title="Descargar"
          >
            ⬇️
          </a>
        ) : null}
      </div>
    </div>
  );
}
export default function CancionCompartir() {
  const navigate = useNavigate();
  const { songId } = useParams();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [videoOk, setVideoOk] = useState(true);

  const androidLink =
    "https://play.google.com/store/apps/details?id=com.alanvillalba.cancionesia&pcampaignid=web_share";

  const goToLanding = () => navigate("/");
  const goToApp = () => navigate("/app"); // ajusta si tu app panel está en otra ruta

  const audio1 = data?.coverUrl || "";
  const audio2 = data?.coverUrl02 || "";
  const videoUrl = data?.videoUrl || "";
  const permitirDescarga = data?.descargar !== false;

  const showAudio1 = useMemo(() => !!audio1, [audio1]);
  const showAudio2 = useMemo(() => !!audio2, [audio2]);
  const showVideo = useMemo(() => !!videoUrl && videoOk, [videoUrl, videoOk]);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setNotFound(false);

      try {
        const ref = doc(db, "cancionesWeb", songId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          if (!alive) return;
          setNotFound(true);
          setData(null);
          setLoading(false);
          return;
        }

        const d = snap.data();
        if (!alive) return;

        setData(d);
        setLoading(false);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setNotFound(true);
        setLoading(false);
      }
    }

    if (songId) load();
    return () => {
      alive = false;
    };
  }, [songId]);

  const songTitle =
  (data?.titulo || data?.title || data?.shareTitle || "").trim() ||
  t("share.song.title");



  return (
    <div className="lp share">
      {/* Top bar */}
      <header className="lp-topbar">
        <div className="lp-container lp-topbar-inner">
          <div
            className="lp-brand"
            role="button"
            onClick={goToLanding}
            tabIndex={0}
          >
            
            <img className="lp-mark-img" src={logoMark} alt={`${t("app.name")} logo`} />
            <span className="lp-brand-name">{t("app.name")}</span>
          </div>
          <LanguageDropdown size="md" showLabel align="left" />       

          <div className="lp-topbar-actions">
            <button className="lp-top-cta" onClick={goToApp}>
              {t("login.title")}
            </button>
            <button className="lp-top-cta" onClick={goToLanding}>
              {t("share.topbar.goWeb")}
            </button>
          </div>
        </div>
      </header>

     

      {/* Content */}
      <section className="share-content">
        <div className="lp-container">
          {loading && <div className="share-box">{t("share.loading")}</div>}

          {!loading && notFound && (
            <div className="share-box">
              <div className="share-box-title">{t("share.notFound.title")}</div>
              <div className="share-box-desc">{t("share.notFound.desc")}</div>
              <div style={{ marginTop: 12 }}>
                <button className="share-btn share-btn-primary" onClick={goToLanding}>
                  {t("share.notFound.back")}
                </button>
              </div>
            </div>
          )}

          {!loading && !notFound && (
            <>
              <h2 className="share-h2">{songTitle}</h2>

              {/* Players */}
              <div className="share-grid">
                {showAudio1 ? (
                  <GoldPlayer title={t("share.song.v1")} src={audio1} t={t} descargar={permitirDescarga} />
                ) : null}

                {showAudio2 ? (
                  <GoldPlayer title={t("share.song.v2")} src={audio2} t={t} descargar={permitirDescarga} />
                ) : null}
              </div>

              {!showAudio1 && !showAudio2 ? (
                <div className="share-box">
                  <div className="share-box-desc">{t("share.song.noAudio")}</div>
                </div>
              ) : null}

              {/* CTA premium debajo del reproductor */}
              <div className="share-cta-box">
                <div className="share-cta-title">{t("share.cta2.title")}</div>
                <div className="share-cta-sub">{t("share.cta2.subtitle")}</div>

                <div className="share-cta-buttons">
                  <a
                    href={androidLink}
                    target="_blank"
                    rel="noreferrer"
                    className="gold-btn gold-btn-primary"
                  >
                    {t("share.cta2.android")}
                  </a>

                  <button
                    type="button"
                    onClick={goToLanding}
                    className="gold-btn gold-btn-ghost"
                  >
                    {t("share.cta2.web")}
                  </button>
                </div>
              </div>

              {/* Video */}
              {showVideo ? (
                <div className="share-videoWrap">
                  <div className="share-videoTitle">{t("share.video.title")}</div>
                  <video
                    className="share-video"
                    controls
                    preload="metadata"
                    onError={() => setVideoOk(false)}
                  >
                    <source src={videoUrl} />
                  </video>
                </div>
              ) : null}

             
            </>
          )}
        </div>
      </section>

      <footer className="share-footer">
        <div className="lp-container">
          <div className="share-footer-tx">{t("share.footer")}</div>
        </div>
      </footer>
    </div>
  );
}
